/**
 * Make calculations based on image geometry
 */
export class ImageSizerService {
	/**
	 * No injections
	 */
	constructor() {
		'ngInject';
		this._matcher = /\/files\/images\/[a-f0-9]{32}\.([\w\.-]+)/;
	}

	/**
	 * Return true if the given geometry string is a recognized format
	 * @param {String} geometry  Size instruction such as fit-100x100
	 * @returns {Boolean}
	 */
	isParseable(geometry) {
		return !!geometry.match(this._matcher);
	}

	/**
	 * Extract a specification from the given geometry
	 * @param {String} geometry Size instruction such as fit-100x100
	 * @returns {Object}
	 * @property {String} resizeMethod  One of pin, fit, exact
	 * @property {Number} boundingWidth
	 * @property {Number} boundingHeight
	 * @property {String} scaleup  Either "on" or "off"
	 */
	parse(geometry) {
		if (!geometry) {
			return false;
		}
		let spec = {};
		let match = geometry.match(/(?:^|\.)(pin|fit|exact|grid)-(\d+)(?:x(\d+))?/);
		if (match) {
			spec.resizeMethod = match[1];
			spec.boundingWidth = +match[2];
			spec.boundingHeight = +match[3];
		} else {
			console.error(
				`ImageSizer: Unknown geometry '${geometry}'; expecting one like 'pin-100', 'fit-100x100', or 'exact-100x100'`
			);
		}
		spec.scaleup = geometry.match(/\.scaleup-on\./) ? 'on' : 'off';
		return spec;
	}

	/**
	 * Given an html and geometry, return the full URL
	 * @param {String} originalUrl  The URL to the original-sized image
	 * @param {String} geometry  geometry size instruction such as fit-100x100, pin-100.jpg, etc.
	 * @return {String}
	 */
	url(originalUrl, geometry) {
		originalUrl = originalUrl.replace(/\.(pin|fit|exact|grid)-(\d+)(?:x(\d+))/, '');
		const match = originalUrl.match(/\.(png|jpe?g|gif)$/i);
		let url = `${originalUrl}.${geometry}`;
		if (match && match[1].toLowerCase() === 'jpeg') {
			url += '.jpg';
		} else if (match) {
			url += match[0];
		} else {
			url += '.jpg';
		}
		if (url.slice(-3).toLowerCase() == 'gif') {
			url += '?useEngine=convert';
		}
		return url;
	}

	/**
	 * Given image width and height, return the image's size after the given geometry is applied
	 * @param {Object} data
	 * @property {Number} width
	 * @property {Number} height
	 * @param {String} geometry Size instruction such as fit-100x100
	 * @returns {[Number,Number]}  The effective width and height in a two-member array
	 */
	getSize(data, geometry) {
		const spec = this.parse(geometry);
		if (!data || !data.width || !data.height || !spec.boundingWidth) {
			return [0, 0];
		}
		const size = this[spec.resizeMethod + 'Size'](data, spec);
		if (!size || isNaN(size[0]) || isNaN(size[1])) {
			throw new Error('ImageSizer.getSize: invalid data or geometry: ' + geometry + '; ' + JSON.stringify(data));
		}
		return size;
	}

	/** @private */
	pinSize(data, spec) {
		let aspect = data.width / data.height;
		let height = Math.round(spec.boundingWidth / aspect);
		return [Math.round(spec.boundingWidth), Math.round(height)];
	}

	/** @private */
	fitSize(data, spec) {
		let srcAspect = data.width / data.height;
		let boundAspect = spec.boundingWidth / spec.boundingHeight;
		// TODO: handle zoom rounding errors?
		// TODO: handle scaleup
		if (srcAspect > boundAspect) {
			// source is more landscape than bounding box: fit to width
			let factor = spec.boundingWidth / data.width;
			return [Math.round(spec.boundingWidth), Math.round(data.height * factor)];
		} else {
			// source is more portrait than bounding box: fit to height
			let factor = spec.boundingHeight / data.height;
			return [Math.round(data.width * factor), Math.round(spec.boundingHeight)];
		}
	}

	/** @private */
	exactSize(data, spec) {
		return [Math.round(spec.boundingWidth), Math.round(spec.boundingHeight)];
	}

	/** @private */
	gridSize(data, spec) {
		let srcAspect = data.width / data.height;
		let boundAspect = spec.boundingWidth / spec.boundingHeight;
		// TODO: handle zoom rounding errors?
		// TODO: handle scaleup
		if (srcAspect > boundAspect) {
			// source is more landscape than bounding box: fit to width (same as fitSize())
			let factor = spec.boundingWidth / data.width;
			return [Math.round(spec.boundingWidth), Math.round(data.height * factor)];
		}
		// otherwise we will be exactly the bounding box size
		return [Math.round(spec.boundingWidth), Math.round(spec.boundingHeight)];
	}

	/** @private */
	originalSize(data, spec) {
		return [Math.round(data.width), Math.round(data.height)];
	}
}
